import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate } from "react-router-dom";
import "../styles/IJOTNewsLetter.css";
import { FileText, MoveUpRight, X } from "lucide-react";
import { MdOutlinePictureAsPdf } from "react-icons/md";

const IJOTNewsLetter = () => {
  const navigate = useNavigate();
  const [tokenState, setTokenState] = useState(localStorage.getItem("token"));
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const handleMemberLogin = () => {
    navigate("/member-login");
  };

  const handleEdcclogin = () => {
    navigate("/edcc-login");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== tokenState){
        setTokenState(currentToken);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [tokenState]);

  // Fetch newsletters from backend
  useEffect(() => {
    const fetchNewsletters = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching from URL:', `${process.env.REACT_APP_BASEURL}/getAllNewsletters`);
        
        const response = await fetch(`${process.env.REACT_APP_BASEURL}/getAllNewsletters`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const apiResponse = await response.json();
        console.log('Full API Response:', apiResponse);
        
        // Check if response is successful and has data
        if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
          console.log('API Data:', apiResponse.data);
          
          // Filter out deleted newsletters and only include active ones
          const activeNewsletters = apiResponse.data.filter(newsletter => {
            console.log('Checking newsletter:', newsletter);
            return !newsletter.isDeleted && newsletter.status === 1;
          });
          
          console.log('Filtered Active Newsletters:', activeNewsletters);
          setNewsletters(activeNewsletters);
        } else {
          console.error('Invalid response structure:', apiResponse);
          throw new Error(apiResponse.message || 'Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching newsletters:', err);
        setError(`Failed to load newsletters: ${err.message}`);
        setNewsletters([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletters();
  }, []);

  // Handle newsletter click to open PDF in viewer
  const handleNewsletterClick = (newsletter) => {
    setSelectedPdf(newsletter);
    setShowPdfModal(true);
  };

  // Close PDF modal
  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setSelectedPdf(null);
  };

  return (
    <>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>

        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
              {!tokenState && (
                <div className="hero-buttons">
                <button className="glass-button" onClick={handleMemberLogin}>
                  Member Login
                </button>
                <button className="glass-button" onClick={handleEdcclogin}>
                  Ergonomic Certificate login
                </button>
              </div>
              )}
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container ijot-newsletter-content-container">
          <section className="ijot-newsletter-section">
            <Container className="mt-5">
              <h2 className="text-center ijot-newsletter-heading mb-5">
                Newsletters
              </h2>

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading newsletters...</p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="alert alert-danger text-center" role="alert">
                  {error}
                  <br />
                  <small>Check console for more details</small>
                </div>
              )}

              {/* Newsletters Grid */}
              {!loading && !error && (
                <Row className="g-4">
                  {newsletters.length > 0 ? (
                    newsletters.map((newsletter, index) => (
                      <Col xs={12} sm={6} md={5} lg={4} key={newsletter._id || index}>
                        <Button 
                          className="newsletter-btn w-100 h-auto p-4 d-flex justify-content-between align-items-center"
                          onClick={() => handleNewsletterClick(newsletter)}
                        >
                          <div className="d-flex align-items-center gap-3 text-start">
                            <MdOutlinePictureAsPdf size={56} className="d-none d-sm-block"/>
                            <span className="newsletter-title">{newsletter.title}</span>
                          </div>
                          <MoveUpRight size={32} className="d-none d-sm-block"/>
                        </Button>
                      </Col>
                    ))
                  ) : (
                    <Col xs={12}>
                      <div className="text-center py-5">
                        <p className="text-muted">No newsletters available at the moment.</p>
                      </div>
                    </Col>
                  )}
                </Row>
              )}
            </Container>
          </section>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      <Modal 
        show={showPdfModal} 
        onHide={handleClosePdfModal} 
        size="xl" 
        centered
        className="pdf-viewer-modal"
      >
        <Modal.Header className="d-flex justify-content-between align-items-center">
          <Modal.Title>{selectedPdf?.title || "PDF Viewer"}</Modal.Title>
          <Button 
            variant="outline-secondary" 
            onClick={handleClosePdfModal}
            className="close-btn"
          >
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedPdf && (
            <div className="pdf-viewer-container">
              <iframe
                src={selectedPdf.file_url}
                title={selectedPdf.title}
                width="100%"
                height="600px"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button className="new-tab"
            variant="primary" 
            onClick={() => window.open(selectedPdf?.file_url, '_blank')}
          >
            Open in New Tab
          </Button>
          <Button variant="secondary" onClick={handleClosePdfModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IJOTNewsLetter;